/**
 * AI preferences — persisted to localStorage so users don't pick their model
 * every reload. Small standalone store; doesn't need React context.
 */

import { useSyncExternalStore } from 'react';
import { DEFAULT_MODEL } from '@/lib/aiModels';

const STORAGE_KEY = 'playground:ai-prefs:v1';

export interface AiPrefs {
  model: string;
}

function loadInitial(): AiPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.model === 'string') return parsed;
    }
  } catch {
    /* ignore */
  }
  return { model: DEFAULT_MODEL };
}

let state: AiPrefs = loadInitial();
const listeners = new Set<() => void>();

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota errors */
  }
}

export function setModel(model: string) {
  state = { ...state, model };
  persist();
  for (const l of listeners) l();
}

export function useAiPrefs(): AiPrefs {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
    () => state
  );
}
